import type { IItem } from "@order-up/models";
import { ItemBaseSchema } from "@order-up/schemas";

/**
 * Lenient partial schema for validating item UPDATE payloads. Updates are
 * partial and the model carries fields the strict schema does not define, so
 * partial() + passthrough(): invalid VALUES on known fields are still caught
 * while extra/missing keys are tolerated (the cross-field refinements live on
 * ItemSchema and intentionally do not apply to partial updates).
 */
const ItemUpdateSchema = ItemBaseSchema.partial().passthrough();

const FIELDS_NOT_VALIDATED = [
  "docId",
  "removedModifierGroupIds",
  "menuSpecificPrices",
] as const;

/** The fields an update will actually write, in the shape the schema reads. */
export const itemUpdateForValidation = (
  itemData: Partial<IItem>,
): Record<string, unknown> => {
  const payload: Record<string, unknown> = { ...itemData };
  for (const field of FIELDS_NOT_VALIDATED) delete payload[field];
  return payload;
};

/**
 * Validate what the update WRITES. Blocking — an invalid value is rejected,
 * never silently written; the Firestore rules enforce no schema.
 *
 * ⚠️ Only the payload is judged, never the stored document. The update MERGES
 * the payload into the stored item, so a stored field the operator did not
 * send is not written by this save — and refusing the save over it locks the
 * operator out of a value they cannot see and cannot change.
 *
 * That is not theoretical. This used to validate the payload merged over the
 * stored item, and the POS writes the WHOLE item from Dart's `Item.toMap()` —
 * `null` for every absent optional (`itemType`, `stockInfo`), and `stockInfo`
 * in Dart's own shape (`status: "inStock"`, nested nulls) that `ItemBaseSchema`
 * does not accept. So once a till 86'd an item, every later management save
 * failed with "Failed to save menu item", including the one putting it back in
 * stock. Measured 2026-10-01 on production: 10 House of Pho items.
 */
export const assertValidItemUpdate = (
  existingItem: object & { name?: string },
  itemData: Partial<IItem>,
): void => {
  const result = ItemUpdateSchema.safeParse(itemUpdateForValidation(itemData));
  if (result.success) return;
  const issues = result.error.issues
    .map((i) => `${i.path.join(".") || "(root)"}: ${i.message}`)
    .join("; ");
  throw new Error(
    `Item "${itemData.name ?? existingItem.name ?? "(unnamed)"}" failed update validation: ${issues}`,
  );
};
